import prepareAssertions from '~/packages/variable/prepareAssertions.util'
import { Variable } from '~/packages/variable/variables.typing'
import { boolean, dateTime, defineAction, enumerated, number, object, string } from '~/prix'
import expression from '~/prix/types/expression'
import { analysis } from '~/prix/types/presets/analysis'
import { assertions } from '~/prix/types/presets/assertions'
import { select } from '~/prix/types/presets/select'
import { childrenType, spaceForPoint } from '~/prix/types/presets/space'
import { timeForIncidence } from '~/prix/types/presets/time'
import { variable } from '~/prix/types/presets/variable'
import legalEntityEadAttendanceStatsAction from './legalEntityEadAttendanceStats.action.json' assert { type: 'json' }
import prefixProperties from '~/prix/utils/prefixProperties'
import courseRegisterIndividualEntity from '~/packages/courseRegisterIndividual/courseRegisterIndividual.entity'
import cnaeDivision from '~/packages/cnaeDivision/cnaeDivision.entity'
import { pick } from '~/prix/utils/types/object'
import getRecommendedCacheOptions from '~/utils/server/stats/getRecommendedCacheOptions'
import { eadAttendanceVariable } from '../stats/eadAttendanceStats.definition'

export const courseProductShape = object({
  productCode: string().label('Código do produto'),
  portfolioSasCode: string().label('Código SAS do portfólio'),
  productType: enumerated({
    course: 'Curso',
    media: 'Mídia',
    trail: 'Trilha',
  }).label('Tipo de produto'),
  productName: string().label('Nome do produto'),
  isActivePortfolio: boolean().label('Portfólio ativo'),
  cnpjCount: number().label('Quantidade de CNPJs'),
})

const combinedShape = object({
  agentCpf: string().label('CPF do agente'),
  startDate: dateTime().label('Data de início do atendimento'),

  //legalEntity
  id: string().label('ID', 'Identificador', 'Código'),
  tradeName: string().label('Nome fantasia'),
  corporateName: string().isOptional().label('Razão social'),
  cnpj: string().label('CNPJ'),
  openingDate: dateTime().label('Data de abertura'),
  email: string().label('Email'),
  street: string().label('Logradouro'),
  city: string().label('Cidade'),
  neighborhood: string().label('Bairro'),
  state: string().label('Estado'),
  registrationStatus: enumerated({
    active: 'Ativo',
    inapt: 'Inapto',
    suspended: 'Suspenso',
    extinct: 'Extinto',
  }).label('Status'),
  registrationStatusReason: string().label('Motivo do status de registro'),
  segmentId: string().label('Identificador do segmento'),
  size: enumerated({
    MEI: 'MEI',
    ME: 'Micro',
    EPP: 'Pequena',
    other: 'Outros',
    unknown: 'Desconhecido',
  }).label('Porte'),
  ageGroup: enumerated({
    startup: 'Startup',
    young: 'Jovem',
    growth: 'Crescimento',
    established: 'Estabelecida',
  }).label('Maturidade'),
  isSimples: boolean().label('É Simples Nacional'),
  capital: number().label('Capital social'),

  fullAddress: string().label('Endereço'),
  fullPhoneNumber1: string().label('Telefone principal'),
  fullPhoneNumber2: string().label('Telefone secundário'),

  // geo
  pointOnStreet: object({
    type: string().label('Tipo'),
    coordinates: object({
      0: number().label('Longitude'),
      1: number().label('Latitude'),
    }),
  }).label('Ponto na rua'),

  //attendant
  cpf: string().label('CPF'),
  name: string().label('Nome do agente'),

  ...prefixProperties('courseRegisterIndividual.', courseRegisterIndividualEntity.schema.shape),
  ...prefixProperties('cnaeDivision.', cnaeDivision.schema.shape),
})
export const legalEntityEadAttendanceExpression = expression(combinedShape.shape)

export const legalEntityEadAttendanceVariable: Variable = {
  key: 'legalEntityEadAttendance',
  categoryKey: 'legalEntityEadAttendance',
  categoryKeys: ['legalEntityEadAttendance'],
  actionDefinitionKey: legalEntityEadAttendanceStatsAction.key,
  title: 'Empresas distintas atendidas pelo EAD',
  label: 'Geral',
  unitSingular: 'empresa',
  unitPlural: 'empresas',
  grammaticalGender: 'female',
  timeType: 'incidence',
  shape: combinedShape,
  betterDensity: 'more',
  relevance: 0.3,
  payload: {
    productName: 'Empresas atendidas pelo EAD',
    portfolioName: ['EAD'],
  },
  basicSelect: ['corporateName', 'startDate'],
  detailedSelect: ['id', 'tradeName', 'cnpj', 'pointOnStreet'],
  filterableBy: expression(pick(combinedShape.shape, ['size', 'ageGroup', 'registrationStatus'])),
  timeSeries: eadAttendanceVariable.timeSeries,
  assertions: prepareAssertions(legalEntityEadAttendanceExpression),
}

const eadAttendanceStatsDefinition = defineAction({
  ...legalEntityEadAttendanceStatsAction,
  input: object({
    variable,
    highlight: legalEntityEadAttendanceExpression.isOptional(),
    filter: legalEntityEadAttendanceExpression.isOptional(),
    space: spaceForPoint().isOptional(),
    time: timeForIncidence().isOptional(),
    select: select(combinedShape),
    assertions,
    analysis,
  }),
  output: object({
    items: object({
      value: number(),
      geoId: string().isOptional(),
      geoType: childrenType.isOptional(),
      dateTime: string().isOptional(),
    }),
  }),
  cache: getRecommendedCacheOptions,
})

export default eadAttendanceStatsDefinition
